import {
  CalendarDays,
  CreditCard,
  DollarSign,
  Gift,
  Handshake,
  HelpCircle,
  House,
  Info,
  type LucideIcon,
  MessageCircle,
  Settings,
  SquareParking,
  Truck,
} from "lucide-react";

export type NavItem = { href: string; label: string; icon: LucideIcon };

/**
 * Collapse entries that resolve to the same `href`, keeping the FIRST occurrence.
 * Nav lists are assembled from several groups (context-specific links + the
 * shared public links), and `/parking` legitimately appears in more than one
 * group — the context-specific entry wins its position, and the duplicate from
 * the public spread drops out. Both renderers key on `href`, so an un-deduped
 * list would also produce duplicate React keys.
 */
function dedupeByHref(items: NavItem[]): NavItem[] {
  const seen = new Set<string>();
  const result: NavItem[] = [];
  for (const item of items) {
    if (seen.has(item.href)) continue;
    seen.add(item.href);
    result.push(item);
  }
  return result;
}

/**
 * Where the navbar's "Sign in" points. The navbar never deep-links the current
 * page: the sign-in flow returns you home, not to the marketing page you
 * happened to be reading.
 */
export const NAV_AUTH_HREF = `/auth?next=${encodeURIComponent("/")}`;

/**
 * Where the signed-out "List your spot" CTA points, in both the navbar and the
 * mobile menu. The route
 * is protected, so the proxy sends a signed-out visitor through `/auth` first
 * and back here afterwards.
 */
export const HOST_ONBOARDING_HREF = "/host/onboarding";

export function getPublicNavItems(): NavItem[] {
  const items: NavItem[] = [];
  // `/` is the host landing page, so hosting needs no nav tab of its own; this
  // slot points at the driver landing page instead.
  items.push({
    href: "/parking",
    label: "Find Parking",
    icon: SquareParking,
  });
  items.push({ href: "/refer", label: "Refer & Earn", icon: Gift });
  items.push({ href: "/partnerships", label: "Partnerships", icon: Handshake });
  items.push({ href: "/about", label: "About Us", icon: Info });
  return items;
}

type MobileSidebarNavParams = {
  currentContext: "host" | "driver";
  driverFeaturesEnabled: boolean;
  pathname: string;
  /** A non-host with money to show; see `useDriverEarningsEntry`. */
  showEarnings?: boolean;
};

export function getMobileSidebarNavLinks({
  currentContext,
  driverFeaturesEnabled,
  pathname,
  showEarnings = false,
}: MobileSidebarNavParams): NavItem[] {
  const isOnboarding = pathname.startsWith("/host/onboarding");
  const isLandingOrSearch =
    pathname === "/parking" || pathname.startsWith("/search");

  return dedupeByHref([
    // Context-independent: the inbox holds both sides of the account.
    { href: "/messages", label: "Messages", icon: MessageCircle },
    ...(currentContext === "host" && !isOnboarding
      ? [
          { href: "/host/listings", label: "Listings", icon: House },
          {
            href: "/host/reservations",
            label: "Bookings",
            icon: CalendarDays,
          },
          // Payouts merged into Earnings — /host/payouts now redirects there.
          {
            href: "/host/earnings",
            label: "Earnings",
            icon: DollarSign,
          },
        ]
      : driverFeaturesEnabled
        ? [
            ...(!isLandingOrSearch
              ? [
                  {
                    href: "/parking",
                    label: "Find a Parking Spot",
                    icon: SquareParking,
                  },
                ]
              : []),
            {
              href: "/driver/bookings",
              label: "Reservations",
              icon: CalendarDays,
            },
          ]
        : []),
    // Not flag-gated: referral money exists without driver features.
    ...(currentContext === "driver" && showEarnings
      ? [{ href: "/account/earnings", label: "Earnings", icon: DollarSign }]
      : []),
    ...(driverFeaturesEnabled
      ? [{ href: "/account/vehicles", label: "Vehicles", icon: Truck }]
      : []),
    ...(driverFeaturesEnabled
      ? [
          {
            href: "/account/payments",
            label: "Payment Methods",
            icon: CreditCard,
          },
        ]
      : []),
    { href: "/account/settings", label: "Account Settings", icon: Settings },
    { href: "/support", label: "Support", icon: HelpCircle },
    ...getPublicNavItems(),
  ]);
}
